import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import * as Icons from 'lucide-react';
import { cn } from '../lib/utils';
import { menuConfig } from '../config/menuConfig';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from './ui/sidebar';

const YOOL_LOGO_URL =
  'https://asset.yooldigital.com/api/assets/181064f0-64b3-48ed-ac67-208283f5c6c7/public';
const YOOLIA_ISOTIPO_URL =
  'https://asset.yooldigital.com/api/assets/4770db76-7851-4f41-a926-12ff22fce650/4770db76-7851-4f41-a926-12ff22fce650.svg';

function NavIcon({ name, active }) {
  if (!name) return null;
  if (name === 'Yoolia') {
    return (
      <img
        src={YOOLIA_ISOTIPO_URL}
        alt="Yoolia"
        className={cn('size-4 shrink-0', active ? 'opacity-100' : 'opacity-55')}
        style={{ filter: 'brightness(0) invert(1)' }}
      />
    );
  }
  const Icon = Icons[name];
  if (!Icon) return null;
  return (
    <Icon
      className={cn('size-4 shrink-0')}
      style={active ? { color: 'var(--fy-dark-pink, #F472B6)' } : undefined}
      strokeWidth={active ? 1.85 : 1.6}
    />
  );
}

// ── Grupo colapsable (controlado desde el padre) ──────────────────────
function CollapsibleGroup({ id, label, icon, items, isActiveFn, isOpen, onToggle }) {
  const hasActive = items.some((it) => isActiveFn(it.path));
  const bodyRef   = useRef(null);
  const firstRender = useRef(true);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (firstRender.current) {
      // Sin animación en el primer render — empezar cerrado
      firstRender.current = false;
      el.style.transition = 'none';
      el.style.maxHeight  = '0px';
      el.style.opacity    = '0';
      requestAnimationFrame(() => {
        el.style.transition = 'max-height 220ms ease, opacity 160ms ease';
      });
      return;
    }
    el.style.maxHeight = isOpen ? el.scrollHeight + 'px' : '0px';
    el.style.opacity   = isOpen ? '1' : '0';
  }, [isOpen]);

  return (
    <SidebarGroup className="p-0 gap-0.5">
      <SidebarMenuItem>
        <SidebarMenuButton
          isActive={hasActive}
          onClick={() => onToggle(id)}
          className="cursor-pointer"
        >
          <NavIcon name={icon} active={hasActive} />
          <span className="flex-1 text-left">{label}</span>
          <Icons.ChevronDown
            className={cn(
              'size-3.5 shrink-0 text-white/40 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </SidebarMenuButton>
      </SidebarMenuItem>

      <div ref={bodyRef} style={{ overflow: 'hidden' }}>
        <SidebarGroupContent className="pl-4">
          <SidebarMenu className="gap-0.5">
            {items.map((item) => {
              const active = isActiveFn(item.path);
              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton asChild isActive={active}>
                    {item.external ? (
                      <a href={item.path} target="_blank" rel="noopener noreferrer">
                        <NavIcon name={item.icon} active={active} />
                        <span>{item.title}</span>
                      </a>
                    ) : (
                      <Link to={item.path || '/'}>
                        <NavIcon name={item.icon} active={active} />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </div>
    </SidebarGroup>
  );
}

// ── AppSidebar ────────────────────────────────────────────────────────
const AppSidebar = () => {
  const location    = useLocation();
  const { signOut } = useAuth();

  // Accordion: solo un grupo abierto a la vez; null = todos cerrados
  const [openGroupId, setOpenGroupId] = useState(null);

  const handleToggle = (id) => {
    setOpenGroupId((prev) => (prev === id ? null : id));
  };

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Rol de la app (publicMetadata de Clerk). Ocultar ítems `adminOnly` es solo
  // cosmético — cada endpoint valida el rol por su cuenta contra el JWT.
  const { user } = useUser();
  const esAdmin = ['admin', 'super_admin'].includes(user?.publicMetadata?.role);

  const { singles, sectionGroups } = useMemo(() => {
    const visible = (it) => !it.adminOnly || esAdmin;

    const s = menuConfig.sections
      .filter(Boolean)
      .filter((s) => s.type === 'single')
      .filter(visible)
      .map((s) => ({ id: s.id, title: s.title, icon: s.icon, path: s.path, external: s.external }));

    const g = menuConfig.sections
      .filter(Boolean)
      .filter((s) => s.type === 'group')
      .filter(visible)
      .map((s) => ({
        id: s.id,
        label: s.title,
        icon: s.icon,
        items: (s.items || [])
          .filter(Boolean)
          .filter(visible)
          .map((it) => ({ id: it.id, title: it.title, icon: it.icon, path: it.path, external: it.external })),
      }))
      // Un grupo cuyos ítems son todos adminOnly no debe quedar vacío en el menú.
      .filter((s) => s.items.length > 0);

    return { singles: s, sectionGroups: g };
  }, [esAdmin]);

  return (
    <Sidebar collapsible="offcanvas">
      <SidebarHeader className="h-16 flex flex-row items-center px-5 border-b border-white/10 shrink-0 gap-0">
        <Link to="/" className="flex items-center gap-2.5 no-underline">
          <img
            src={YOOL_LOGO_URL}
            alt="Yool"
            className="h-[22px] w-auto opacity-90"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <span className="text-[12.5px] font-semibold text-white/50 border-l border-white/20 pl-2.5 tracking-[-0.01em]">
            Admin
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-3 py-4 overflow-y-auto flex flex-col gap-0.5">
        {singles.length > 0 && (
          <SidebarGroup className="p-0 gap-0">
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {singles.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton asChild isActive={active}>
                        {item.external ? (
                          <a href={item.path} target="_blank" rel="noopener noreferrer">
                            <NavIcon name={item.icon} active={active} />
                            <span>{item.title}</span>
                          </a>
                        ) : (
                          <Link to={item.path || '/'}>
                            <NavIcon name={item.icon} active={active} />
                            <span>{item.title}</span>
                          </Link>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {sectionGroups.map((group) => (
          <CollapsibleGroup
            key={group.id}
            id={group.id}
            label={group.label}
            icon={group.icon}
            items={group.items}
            isActiveFn={isActive}
            isOpen={openGroupId === group.id}
            onToggle={handleToggle}
          />
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-white/10 p-3 gap-1">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => signOut()}>
              <Icons.LogOut className="size-4" strokeWidth={1.6} />
              <span>Cerrar Sesión</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <p className="text-center text-[10.5px] font-bold tracking-[-0.005em] leading-[1.3] py-1.5 px-3">
          <span style={{ color: 'var(--fy-dark-indigo)' }}>Simple. </span>
          <span style={{ color: 'var(--fy-dark-pink, #F472B6)' }}>Rápido. </span>
          <span style={{ color: 'var(--fy-dark-yellow, #F5C400)' }}>100% Digital.</span>
        </p>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
